import { styled } from '@mui/system'
import { get, reduce } from 'es-toolkit/compat'
import FieldHarvestRow from './field-harvest-row'
import { hideLayerLegend } from 'actions/map'
import { useCallback, useEffect, useMemo } from 'react'
import { editFieldName, cancelEditFieldName } from 'actions/field'
import { startEditField } from 'actions/map'
import { showModal } from 'actions/app'
import { editField } from 'api/field'
import { ErrorOutlineOutlined as ErrorIcon } from '@mui/icons-material'
import ArchiveField from 'components/archive-field'
import { useState } from 'react'
import { getHarvestStatusIcon } from 'common/c'
import colors from 'common/colors'
import HarvestPanel from './harvest-panel.tsx'
import { useSelector } from 'react-redux'
import EditPreviousYear from './edit-previous-year'
import { trackEvent } from 'api/usage-tracking'

export default function FieldExplorer(props) {
  const permissions = useSelector((state) => state.account.permissions)
  const [showPermissions, setShowPermissions] = useState({})

  const cffEnabled = useSelector(
    (state) => state.account.singleton.prefs?.cff?.enabled ?? false,
  )
  const selectedFarms = useSelector((state) => state.farm.selectedFarms)

  const harvestYears = useSelector((state) => state.harvest.years)
  const fieldEdit = useSelector((state) => get(state, 'map.editField', false))
  const selectedYear = useSelector((state) => state.field.year)
  const editName = useSelector((state) =>
    get(state, 'field.editFieldName', false),
  )
  useEffect(() => {
    setShowPermissions({
      editCropCalibration: permissions.includes('harvest:admin'),
      editCropType: permissions.includes('farm:write'),
      editSensorCalibration: permissions.includes('harvest:admin'),
      editTimeDelay: permissions.includes('harvest:admin'),
      exportHarvest: permissions.includes('farm:read'),
      refreshHarvest: permissions.includes('harvest:admin'),
      setHeader: permissions.includes('harvest:write'),
      updateTare: permissions.includes('harvest:admin'),
      updateTestWeight: permissions.includes('harvest:write'),
      updateTotalYield: permissions.includes('harvest:write'),
    })
  }, [permissions])

  const {
    fields,
    hideEmptyFields = false,
    selectedField,
    harvests = [],
    onFieldSelected,
    onSetHeader,
    onChangeTY,
    onChangeTestWeight,
    onEditSensorCal,
    onUpdateCropCal,
    onCropTypeOverride,
    onChangeTimeDelay,
    onRefreshHarvest,
    onChangeHarvestYear,
  } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const layers = useSelector((state) => get(state, 'field.layers'))
  const [isHarvestActionDrawerOpen, setIsHarvestActionDrawerOpen] =
    useState(false)
  const [harvestOptionsAnchor, setHarvestOptionsAnchor] = useState(null)

  // Use the harvestCount from each field object directly (already filtered by API based on crop)
  const getHarvestCount = useCallback(
    (fieldId) => {
      const field = fields.find((f) => f.id === fieldId)
      return field?.harvestCount ?? 0
    },
    [fields],
  )

  const orderedFields = useMemo(() => {
    // Filter and sort fields with harvests
    const fieldsWithHarvests = fields
      .filter((field) => getHarvestCount(field.id) > 0)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    // Filter and sort fields without harvests
    const fieldsWithoutHarvests = fields
      .filter((field) => getHarvestCount(field.id) === 0)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    // If CFF is enabled, filter by selected farms
    let filteredWithHarvests = fieldsWithHarvests
    let filteredWithoutHarvests = fieldsWithoutHarvests
    if (typeof cffEnabled === 'boolean' && cffEnabled) {
      if (selectedFarms.length === 0) {
        return []
      }
      const selectedFarmIds = selectedFarms.map((farm) => farm?.id)
      filteredWithHarvests = fieldsWithHarvests.filter((field) =>
        selectedFarmIds.includes(field.farmId),
      )
      filteredWithoutHarvests = fieldsWithoutHarvests.filter((field) =>
        selectedFarmIds.includes(field.farmId),
      )
    }

    if (hideEmptyFields) {
      return filteredWithHarvests
    }
    return filteredWithHarvests.concat(filteredWithoutHarvests)
  }, [fields, getHarvestCount, hideEmptyFields, cffEnabled, selectedFarms])

  const shouldShowLegend = useMemo(
    () =>
      reduce(
        layers,
        (result, value, key) => {
          return result || (key === 'boundary' ? false : value)
        },
        false,
      ),
    [layers],
  )

  useEffect(() => {
    if (!shouldShowLegend) dispatch(hideLayerLegend())
  }, [shouldShowLegend])

  const editHarvest = useCallback((editType, h) => {
    trackEvent(editType, {
      cropYear: h.cropYear,
    })
    switch (editType) {
      case 'set_header':
        onSetHeader()
        break
      case 'edit_crop_type':
        onCropTypeOverride()
        break
      case 'change_ty':
        onChangeTY()
        break
      case 'change_tw':
        onChangeTestWeight()
        break
      case 'edit_sensor_calibration':
        onEditSensorCal()
        break
      case 'edit_crop_calibration':
        onUpdateCropCal()
        break
      case 'edit_time_delay':
        onChangeTimeDelay()
        break
      case 'refresh':
        onRefreshHarvest(h)
        break
      case 'change_harvest_year':
        onChangeHarvestYear()
        break
      default:
        return
    }
  })

  const handleFieldPress = useCallback((field, isOpen) => {
    const element = document.getElementById('pageContent')

    if (isOpen && fieldEdit) {
      dispatch(editFieldName())
    } else if (isOpen) {
      onFieldSelected(null)
      setIsHarvestActionDrawerOpen(false)
      setHarvestOptionsAnchor(null)
    } else {
      onFieldSelected(field)
      setIsHarvestActionDrawerOpen(true)
      if (element) {
        setHarvestOptionsAnchor(element)
      }
    }
  })

  const getFieldName = (field, open = false) => {
    return (
      <>
        <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {field.name}
        </div>
        {open &&
        harvests.length &&
        harvests.find((h) => h.fieldId === field.id) ? (
          getHarvestStatus()
        ) : (
          <StyledHarvestCount>
            {' '}
            ({getHarvestCount(field.id)})
          </StyledHarvestCount>
        )}
      </>
    )
  }

  const getHarvestStatus = () => {
    const harvestStatuses = harvests
      .map((h) => {
        if (selectedField && selectedField.id === h.fieldId) {
          return h.status
        }
      })
      .filter(Boolean)
    const harvestErrorStatuses = [
      'DATA_RECEIVED_ERROR',
      'LAYER_PROCESSING_NO_LAYERS',
      'LAYER_PROCESSING_ERROR',
    ]
    const harvestProcessingStatuses = [
      'LAYER_PROCESSING_STARTED',
      'DATA_RECEIVED',
      'REFRESH_REQUESTED',
      'AUTO_SYNC_PENDING',
    ]

    let statusIcon = (
      <ErrorIcon
        size="small"
        style={{ color: colors.dangerAccent, height: '0.7em' }}
        title={t('error')}
        titleAccess={t('error')}
      />
    )
    const errorStatusesFound = harvestStatuses.find((h) =>
      harvestErrorStatuses.find((e) => e == h),
    )
    if (errorStatusesFound) {
      return statusIcon
    } else if (harvestStatuses.every((s) => s === harvestStatuses[0])) {
      // Check if all the statuses are the same
      return getHarvestStatusIcon(harvestStatuses[0])
    } else {
      const processingStatusFound = harvestStatuses.find((h) =>
        harvestProcessingStatuses.find((e) => e == h),
      )
      if (processingStatusFound) {
        return getHarvestStatusIcon(processingStatusFound)
      }
    }
  }
  const onEditField = useCallback(
    (field, isOpen, event) => {
      event.stopPropagation()
      event.preventDefault()
      if (!isOpen) {
        dispatch(cancelEditFieldName())

        const element = document.getElementById('pageContent')
        setHarvestOptionsAnchor(element)
      }
      const currentYear = new Date().getFullYear()
      if (currentYear !== selectedYear) {
        dispatch(
          showModal(
            <EditPreviousYear
              year={selectedYear}
              editCallBack={editFieldInternal}
              field={field}
              isOpen={isOpen}
            />,
          ),
        )
      } else {
        editFieldInternal(field, isOpen)
      }
      event.stopPropagation()
    },
    [selectedYear],
  )

  const editFieldInternal = useCallback((field, isOpen) => {
    dispatch(startEditField())
    if (!isOpen) {
      setIsHarvestActionDrawerOpen(true)
      onFieldSelected(field)
    }
  })

  const onDeleteField = useCallback((id, isOpen, name, event) => {
    if (id != undefined && name != undefined) {
      event.stopPropagation()
      dispatch(
        showModal(<ArchiveField id={id} fieldName={name} />, {
          title: t('archive_item', { item: t('field') }),
        }),
      )
    }
  })
  const onCancelEdit = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(cancelEditFieldName())
  })

  const onSaveName = useCallback(
    (id, name, farmId) => {
      dispatch(editField(id, { name, farmId }))
    },
    [editName],
  )

  return (
    <StyledRoot
      style={{
        paddingTop: '18px',
        justifyContent: 'space-between',
        visibility: 'visible',
      }}
      className="is_visible"
    >
      <StyledList>
        {orderedFields && orderedFields.length ? (
          orderedFields.map((field) => {
            const isOpen = selectedField?.id === field.id
            return (
              <FieldHarvestRow
                id={`${field.name}-${field.id}`}
                key={field.id}
                title={getFieldName(field, isOpen)}
                open={isOpen}
                onEditField={(event) => {
                  onEditField(field, isOpen, event)
                }}
                onDeleteField={(event) => {
                  onDeleteField(field.id, isOpen, field.name, event)
                }}
                fieldName={field.name}
                onClick={(event) => {
                  handleFieldPress(field, isOpen, event)
                }}
                saveName={onSaveName}
                farmId={field?.farmId}
                cancelEdit={onCancelEdit}
                edit={editName && isOpen}
                fieldId={field.id}
                position={'fixed'}
              >
                <HarvestPanel
                  open={isHarvestActionDrawerOpen && !fieldEdit}
                  anchorEl={harvestOptionsAnchor}
                  permissions={showPermissions}
                  onClickHandler={editHarvest}
                  fieldId={field.id}
                />
              </FieldHarvestRow>
            )
          })
        ) : (
          <div>
            <p>
              {t('no_harvests')} / {t('no')} {t('fields')}
            </p>
          </div>
        )}
      </StyledList>
    </StyledRoot>
  )
}

const StyledRoot = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  marginBottom: 0,
  borderRadius: '0.25rem',
  overflowY: 'auto',
})

const StyledHarvestCount = styled('span')({
  opacity: 0.28,
  marginLeft: '0.25em',
})

const StyledList = styled('ul')({
  margin: '18px 0 0 0',
  overflow: 'hidden',
  overflowY: 'auto',
  padding: '1px 11px',
  top: '70px',
  bottom: '8px',
  listStyleType: 'none',
})
